/**
 * directPrintFromResponse
 * =======================
 * Tenta la stampa "fast-path" (sincrona dal browser, come Test Diretto) usando i
 * direct_payloads ritornati dal backend nella response di /orders/{id}/send o
 * /customer/order/send.
 *
 * Logica:
 *  - Per ogni payload, se il browser CORRENTE ha pairata la stampante (vendor/product
 *    USB o BT device id matching), stampa direttamente con webusb.printJob.
 *  - Se NON ha il pairing, ignora silenziosamente: il WS broadcast del backend
 *    arriverà al browser pairato (es. PC cucina) e farà partire la stampa lì.
 *  - Dopo stampa OK, manda ACK al backend così il job in DB diventa 'delivered'
 *    e il task `_check_missed_print_jobs` non lo segnerà come perso.
 *  - Stampe seriali (await sequenziale): nessuna race condition USB.
 */
import { webusb, webbt } from "@/lib/browserPrinting";
import { markJobProcessed, tryAcquirePrintLock } from "@/lib/processedJobs";

async function ackBackend(jobId, success, errorMsg) {
    try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/print/ack/${jobId}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("rm_token") || ""}`,
            },
            body: JSON.stringify({ success, error: errorMsg || "" }),
            keepalive: true,
        });
    } catch (_e) { /* best effort */ }
}

/**
 * @param {Array<{job, match, printer_name}>} directPayloads - dal response del backend
 * @returns {Promise<{tried: number, ok: number, skipped: number, failed: number}>}
 */
export async function tryDirectPrint(directPayloads) {
    const stats = { tried: 0, ok: 0, skipped: 0, failed: 0 };
    if (!Array.isArray(directPayloads) || directPayloads.length === 0) return stats;

    // ⚠️ SERIALIZZAZIONE: stampe await sequenziali per evitare race condition USB
    // (claimInterface single-claim). Stesso comportamento del BrowserPrintAgent.
    for (const dp of directPayloads) {
        stats.tried++;
        const { job, match } = dp || {};
        if (!job || !match) { stats.skipped++; continue; }
        // ⚠️ Lock cross-tab: se un altro tab dello stesso PC sta già stampando, skip
        if (!tryAcquirePrintLock(job.id)) {
            markJobProcessed(job.id);
            stats.skipped++;
            continue;
        }
        markJobProcessed(job.id);
        try {
            if (match.protocol === "usb" && match.usb_vendor_id && match.usb_product_id) {
                if (!webusb.supported()) { stats.skipped++; continue; }
                const dev = await webusb.findPairedDevice(match.usb_vendor_id, match.usb_product_id);
                if (!dev) {
                    // Questo browser non ha il pairing → cade al WS broadcast (gestito da BrowserPrintAgent altro PC)
                    stats.skipped++;
                    continue;
                }
                await webusb.printJob(job, match.usb_vendor_id, match.usb_product_id);
                await ackBackend(job.id, true);
                stats.ok++;
                // Micro-delay per dare tempo al device USB di rilasciare l'interfaccia
                await new Promise(r => setTimeout(r, 150));
            } else if (match.protocol === "bluetooth" && match.bt_device_id) {
                if (!webbt.supported()) { stats.skipped++; continue; }
                const dev = await webbt.findPairedDevice(match.bt_device_id);
                if (!dev) { stats.skipped++; continue; }
                await webbt.printJob(job, match.bt_device_id);
                await ackBackend(job.id, true);
                stats.ok++;
                await new Promise(r => setTimeout(r, 150));
            } else {
                stats.skipped++;
            }
        } catch (err) {
            console.error("[fast-print] Stampa diretta fallita:", err);
            await ackBackend(job.id, false, err.message || String(err));
            stats.failed++;
            // Se fallisce qui, il BrowserPrintAgent NON ritenterà (l'ACK ha già marcato
            // il job come failed in DB). Il toast `print_problem` arriverà via WS al
            // terminale che ha inviato.
        }
    }
    return stats;
}
